/**
 * Where each application in the suite lives, in ONE place.
 *
 * IT WAS INLINE IN THE SITES MENU, seven `process.env.REACT_APP_*_URL || "https://..."`
 * expressions among the cards that use them. That was fine while the menu was the only thing that
 * needed to know — the inbox is the second, and a second copy of a hostname map is how one app
 * ends up linking somewhere the other does not. The notification service already carries a comment
 * about exactly this failure, having been copied three times.
 *
 * KEYED ON THE NAME A NOTIFICATION USES. `NOTIFICATION.APPLICATIONS` holds values from a fixed
 * list — QMS, Developer, SBOM, DataCatalog, Docs — so the inbox can look a row's application
 * straight up here. Administration and Reporting are included because they are suite applications
 * and a notification may one day name them; a key with no notifications is harmless, and a
 * notification naming a key that is absent is a row the reader cannot act on.
 */

export type SuiteApplication = {
    /** The label a person reads. */
    name: string;
    /** The origin, overridable per environment the same way the Sites menu does it. */
    url: string;
};

export const SUITE_APPLICATIONS: Record<string, SuiteApplication> = {
    QMS: {name: "QMS", url: process.env.REACT_APP_FOUNDATION_URL || "https://app.cres-intelligence.io"},
    Docs: {name: "ParkourAI Documents", url: process.env.REACT_APP_DOCS_URL || "https://docs.cres-intelligence.io"},
    SBOM: {name: "SBOM", url: process.env.REACT_APP_SBOM_URL || "https://sbom.cres-intelligence.io"},
    DataCatalog: {name: "Data Catalog", url: process.env.REACT_APP_DATA_CATALOG_URL || "https://dc.cres-intelligence.io"},
    Developer: {name: "Developer", url: process.env.REACT_APP_DEVELOPER_URL || "https://developer.cres-intelligence.io"},
    Administration: {name: "Administration", url: process.env.REACT_APP_ADMINISTRATION_URL || "https://admin.cres-intelligence.io"},
    Reporting: {name: "Reporting", url: process.env.REACT_APP_REPORTING_URL || "https://reports.cres-intelligence.io"},
};

/**
 * The application a notification belongs to, or null when it names none or names one we cannot
 * place.
 *
 * A NOTIFICATION FOR EVERY APPLICATION HAS A NULL LIST — a maintenance banner, say — and belongs
 * nowhere in particular, so it gets no destination rather than being sent to an arbitrary one.
 */
export function applicationOf(applications?: string[] | null): SuiteApplication | null {
    const named = (applications || []).find(one => SUITE_APPLICATIONS[one]);
    return named ? SUITE_APPLICATIONS[named] : null;
}
